import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { styled } from 'baseui'
import BackgroundImage from '@assets/images/bg_login.jpg'
import AiModelSignUpPoster from '@assets/images/createV6/ai-model-sign-up.jpg'
import AiModelSignUpVideo from '@assets/images/createV6/ai-model-sign-up.mp4'
import PhotoshootSignInPoster from '@assets/images/createV6/photoshoot-sign-in.jpg'
import PhotoshootSignInVideo from '@assets/images/createV6/photoshoot-sign-in.mp4'
import Icons from '../../../Icons'
import { lightTheme } from '@/customTheme'
import { useAppDispatch } from '@store/store'
import { setOpenModalTryPremium, setUser } from '@/store/slices/user/actions'
import { SignInManager } from '../SignInManager'
import { useTranslation } from 'react-i18next'
import { customAmplitude } from '@/utils/customAmplitude'
import { useLocation } from 'react-router-dom'
import LogoIcon from '@assets/images/logo_only.svg'
import { useEditorContext } from '@/scenes/engine'
import { useSelector } from 'react-redux'
import { selectOpenModalTryPremium } from '@/store/slices/user/selectors'
import { useSocialLogin } from './useSocialLogin'
import SocialLoginButton from './SocialLoginButton'
import SignInWithEmailScreen from './SignInWithEmail/SignInWithEmailScreen'
import useModal from '@/hooks/useModal'
import { ShowModalType } from '@/scenes/engine/common/constants'
import { setShowedRemoveObjectTutorial } from '@/utils/tutorial'
import { getExperimentVariant } from '@/utils/experimentClient'
import SignInWithPasswordScreen from './SignInWithPassword/SignInWithPasswordScreen'
import SignUpWithPasswordScreen from './SignInWithPassword/SignUpWithPasswordScreen'
import {
  buildSignUpClickProperties,
  buildPremiumPromptProperties,
  buildSignUpPromptProperties,
  buildSignupAnalyticsContext,
} from '@/utils/signupPremiumAnalytics'
import { MOBILE_MEDIA_QUERY } from '@/constants/responsive'

type SignInWelcomeVariant = 'aiModels' | 'photoshoots' | 'general'
type SignInBackgroundMedia = { video: string; poster: string }

const welcomeTitleStyle: CSSProperties = {
  marginBottom: 0,
  marginTop: '32px',
  ...lightTheme.typography.Small14regular,
  lineHeight: '22px',
  letterSpacing: '0.07px',
}

const welcomeSubtitleStyle: CSSProperties = {
  marginBottom: 0,
  marginTop: '8px',
  ...lightTheme.typography.Header26bold,
}

const signInWelcomeVariantContent: Record<
  SignInWelcomeVariant,
  { subtitleI18nKey: string; backgroundMedia: SignInBackgroundMedia | null }
> = {
  aiModels: {
    subtitleI18nKey: 'Put your products on real-looking models',
    backgroundMedia: { video: AiModelSignUpVideo, poster: AiModelSignUpPoster },
  },
  photoshoots: {
    subtitleI18nKey: 'Give your products a full photoshoot',
    backgroundMedia: { video: PhotoshootSignInVideo, poster: PhotoshootSignInPoster },
  },
  general: {
    subtitleI18nKey: 'Your products, looking their best. In seconds.',
    backgroundMedia: null,
  },
}

const SignInRoot = styled('div', {
  width: '100vw',
  height: '100vh',
  maxWidth: '920px',
  maxHeight: '652px',
  flexDirection: 'row',
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'absolute',
  display: 'flex',
  [MOBILE_MEDIA_QUERY]: {
    height: '100dvh',
    maxWidth: '100vw',
    maxHeight: '100dvh',
    borderRadius: 0,
  },
})

const SignInForm = styled('div', (props: { $alignToTop: boolean }) => ({
  width: '50%',
  height: '100%',
  boxSizing: 'border-box',
  padding: '50px 64px',
  background: lightTheme.colors.white,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: props.$alignToTop ? 'flex-start' : 'center',
  alignItems: 'center',
  textAlign: 'center',
  position: 'relative',
  [MOBILE_MEDIA_QUERY]: {
    width: '100%',
    padding: '24px 24px max(24px, env(safe-area-inset-bottom))',
    overflowY: 'auto',
    justifyContent: props.$alignToTop ? 'flex-start' : 'safe center',
  },
}))

const SignInVisual = styled('div', (props: { $hasBackgroundMedia: boolean }) => ({
  width: '50%',
  ...(props.$hasBackgroundMedia ? {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: lightTheme.colors.grayScale20,
  } : {}),
  [MOBILE_MEDIA_QUERY]: {
    display: 'none',
  },
}))

const SocialLoginError = styled('p', {
  width: '100%',
  margin: '0 0 12px',
  ...lightTheme.typography.Small12medium,
  color: lightTheme.colors.negative,
})

function SignIn({ isOpen, close, setLoggin, handleOpenTutorial, isFirstTime = false }) {
  const inputRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const editor = useEditorContext().editor
  const openModalTryPremium = useSelector(selectOpenModalTryPremium)
  const location = useLocation()
  const { t } = useTranslation()
  const [showEmailScreen, setShowEmailScreen] = useState(false)
  const { setShowModalType } = useModal()
  const [showOnBoarding, setShowOnBoarding] = useState(true)
  const [isNoIntroVariant, setIsNoIntroVariant] = useState(false)
  const [showPasswordScreen, setShowPasswordScreen] = useState(false)
  const [showPasswordSignUpScreen, setShowPasswordSignUpScreen] = useState(false)

  const getCurrentSignupAnalyticsContext = useCallback(
    () =>
      buildSignupAnalyticsContext({
        source: isFirstTime
          ? 'signup-1st'
          : (openModalTryPremium.source ??
            (location.pathname.includes('create') ? 'Create Header_CTA' : 'Header_CTA')),
        type: openModalTryPremium.source ? 'Metsikon' : 'Standard',
        pathname: location.pathname,
      }),
    [isFirstTime, openModalTryPremium.source, location.pathname],
  )

  const resetAuthScreens = () => {
    setShowEmailScreen(false)
    setShowPasswordScreen(false)
    setShowPasswordSignUpScreen(false)
  }

  const trackSignupClick = useCallback(
    (destination: 'Email' | 'EmailPassword' | 'EmailPasswordSignup') => {
      customAmplitude(
        'Sign up click',
        buildSignUpClickProperties({
          context: getCurrentSignupAnalyticsContext(),
          destination,
        }),
      )
    },
    [getCurrentSignupAnalyticsContext],
  )

  const openEmailScreen = () => {
    trackSignupClick('Email')
    setShowEmailScreen(true)
    setShowPasswordScreen(false)
    setShowPasswordSignUpScreen(false)
  }

  const openPasswordSignInScreen = () => {
    trackSignupClick('EmailPassword')
    setShowEmailScreen(true)
    setShowPasswordScreen(true)
    setShowPasswordSignUpScreen(false)
  }

  const openPasswordSignUpScreen = () => {
    trackSignupClick('EmailPasswordSignup')
    setShowEmailScreen(true)
    setShowPasswordScreen(true)
    setShowPasswordSignUpScreen(true)
  }

  const backFromPasswordSignInScreen = () => {
    setShowEmailScreen(true)
    setShowPasswordScreen(false)
    setShowPasswordSignUpScreen(false)
  }

  const backFromPasswordSignUpScreen = () => {
    setShowEmailScreen(true)
    setShowPasswordScreen(true)
    setShowPasswordSignUpScreen(false)
  }

  useEffect(() => {
    let cancelled = false
    getExperimentVariant('no-intro-slides').then(value => {
      if (!cancelled) setIsNoIntroVariant(value === 'no-intro-slides')
    })
    return () => { cancelled = true }
  }, [])

  // Shared success handler for all login methods
  const handleLoginSuccess = (user: any) => {
    if (!user) {
      // No user returned - for isFirstTime, still show onboarding
      if (isFirstTime) {
        if (showOnBoarding) {
          setShowModalType(ShowModalType.ONBOARDING)
        } else {
          setShowModalType(null)
        }
      }
      return
    }

    resetAuthScreens()
    setLoggin(true)
    dispatch(setUser(user))

    if (isFirstTime && isNoIntroVariant) {
      localStorage.setItem('showedOnboarding', JSON.stringify(true))
      localStorage.setItem('showedOnboardingDate', new Date().toISOString())
      setShowedRemoveObjectTutorial()
    }
    
    const isPremium = Boolean(user?.userIsPremium)
    if (isPremium) {
      dispatch(setOpenModalTryPremium({ ...openModalTryPremium, isOpen: false }))
      openModalTryPremium.callback?.()
      editor?.handlers.autoSaveHandler.save()
    } else if (openModalTryPremium.source) {
      dispatch(setOpenModalTryPremium({ ...openModalTryPremium, isOpen: true }))
    }
    
    if (isFirstTime) {
      if (showOnBoarding) {
        setShowModalType(ShowModalType.ONBOARDING)
      } else {
        setShowModalType(null)
        if (!isPremium && !openModalTryPremium.opened) {
          const source = location.pathname.includes('create') ? 'Create' : 'Editor Open'
          const premiumPromptProperties = buildPremiumPromptProperties(
            buildSignupAnalyticsContext({
              source,
              type: 'Metsikon',
              pathname: location.pathname,
            }),
          )
          dispatch(
            setOpenModalTryPremium({
              isOpen: true,
              source,
              opened: true,
            })
          )
          customAmplitude('Premium Prompt', premiumPromptProperties)
          // @ts-ignore
          window.dataLayer.push({ event: 'premium_prompt', ...premiumPromptProperties })
        }
      }
    } else {
      close()
    }
  }

  const { login, loadingSocial, socialError } = useSocialLogin({
    onSuccess: handleLoginSuccess,
    getAnalyticsContext: getCurrentSignupAnalyticsContext,
  })

  useEffect(() => {
    SignInManager.getInstance().setupUI(inputRef.current)
  }, [])

  useEffect(() => {
    if (isFirstTime) {
      const urlParams = new URLSearchParams(window.location.search)
      const ob = urlParams.get('ob')
      const alreadyShowedOnboarding = localStorage.getItem('showedOnboarding') ? JSON.parse(localStorage.getItem('showedOnboarding')) : false
      if (ob === '0' || alreadyShowedOnboarding || isNoIntroVariant) {
        setShowOnBoarding(false)
      }
    }
  }, [isFirstTime, isNoIntroVariant])

  useEffect(() => {
    if (isOpen) {
      customAmplitude('Sign up Prompt', buildSignUpPromptProperties(getCurrentSignupAnalyticsContext()))
    }
  }, [isOpen, getCurrentSignupAnalyticsContext])

  useEffect(() => {
    const signInManager = SignInManager.getInstance()
    if (!isOpen) {
      signInManager.setSignupAnalyticsContext(null)
      return
    }
    signInManager.setSignupAnalyticsContext(getCurrentSignupAnalyticsContext())
  }, [isOpen, getCurrentSignupAnalyticsContext])

  const signInWelcomeVariant: SignInWelcomeVariant = location.pathname.includes('/create/ai-models')
    ? 'aiModels'
    : location.pathname.includes('/create/photoshoots')
      ? 'photoshoots'
      : 'general'

  const variantContent = signInWelcomeVariantContent[signInWelcomeVariant]

  const welcomeTitle: { text: string; style: CSSProperties } = {
    text: t('Welcome to Bazaart'),
    style: welcomeTitleStyle,
  }

  const welcomeSubtitle: { text: string; style: CSSProperties } = {
    text: t(variantContent.subtitleI18nKey),
    style: welcomeSubtitleStyle,
  }

  const backgroundMedia = variantContent.backgroundMedia
  const alignFormToTop = showEmailScreen || showPasswordScreen || showPasswordSignUpScreen

  return (<>
    <SignInRoot
      id="signin"
    >
      <SignInForm
        ref={inputRef}
        $alignToTop={alignFormToTop}
      >
        {showPasswordSignUpScreen ? (
          <SignUpWithPasswordScreen
            onBack={backFromPasswordSignUpScreen}
            onSignIn={backFromPasswordSignInScreen}
            onSuccess={handleLoginSuccess}
          />
        ) : showPasswordScreen ? (
          <SignInWithPasswordScreen
            onBack={backFromPasswordSignInScreen}
            onSuccess={handleLoginSuccess}
            onSignUp={openPasswordSignUpScreen}
          />
        ) : !showEmailScreen ? (
          <>
            <img 
             alt="logo"
             style={{
               width: 'fite-content',
               height: '54px',
             }}
            src={LogoIcon}/>

            <h2 style={welcomeTitle.style}>
              {welcomeTitle.text}
            </h2>
            <h3 style={welcomeSubtitle.style}>
              {welcomeSubtitle.text}
            </h3>

            <SocialLoginButton
              style={{
                position: 'relative',
                ...lightTheme.typography.Small14Semibold,
                color: lightTheme.colors.blackGray,
                marginBottom: 0,
                marginTop: '32px',
                ':hover': {
                  background: lightTheme.colors.grayScale100,
                },
              }}
              isLoading={loadingSocial === 'google'}
              disabled={!!loadingSocial && loadingSocial !== 'google'}
              onClick={async () => {
                await login('google')
              }}
            >
              <Icons.GGIcon style={{ position: 'absolute', left: '12px' }} />
              <>{t('Continue with Google')}</>
            </SocialLoginButton>
            <SocialLoginButton
              style={{
                position: 'relative',
                ...lightTheme.typography.Small14Semibold,
                background: 'rgba(57, 117, 234, 1)',
                color: '#fff',
                marginBottom: 0,
                marginTop: '16px',
                ':hover': {
                  background: 'rgba(51, 105, 211, 1)',
                },
              }}
              isLoading={loadingSocial === 'facebook'}
              disabled={!!loadingSocial && loadingSocial !== 'facebook'}
              onClick={async () => {
                await login('facebook')
              }}
            >
              <Icons.FbIcon
                style={{
                  position: 'absolute',
                  left: '12px',
                }}
              />
              <>{t('Continue with Facebook')}</>
            </SocialLoginButton>
            <SocialLoginButton
              style={{
                position: 'relative',
                ...lightTheme.typography.Small14Semibold,
                background: lightTheme.colors.blackGray,
                color: '#fff',
                marginBottom: 0,
                marginTop: '16px',
                ':hover': {
                  background: 'rgba(26, 26, 26, 1)',
                },
              }}
              isLoading={loadingSocial === 'apple'}
              disabled={!!loadingSocial && loadingSocial !== 'apple'}
              onClick={async () => {
                await login('apple')
              }}
            >
              <Icons.AppleIcon style={{ position: 'absolute', left: '12px' }} />
              <>{t('Continue with Apple')}</>
            </SocialLoginButton>
            
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '16px 0', width: '100%'}}>
              <span style={{flex: 1, background: lightTheme.colors.grayScale100, height: '1px'}}></span>
              <span style={{...lightTheme.typography.Small12medium, color: lightTheme.colors.text.text_200_disable, margin: '0 16px'}}>{t('OR')}</span>
              <span style={{flex: 1, background: lightTheme.colors.grayScale100, height: '1px'}}></span>
            </div>
            <SocialLoginButton
              style={{
                position: 'relative',
                ...lightTheme.typography.Small14Semibold,
                border: `1px solid ${lightTheme.colors.grayScale100}`,
                color: lightTheme.colors.blackGray,
                marginBottom: '12px',
                background: lightTheme.colors.grayScale0,
                ':hover': {
                  border: `1px solid transparent`,
                  background: lightTheme.colors.grayScale50,
                },
              }}
              disabled={!!loadingSocial}
              onClick={() => {
                openEmailScreen()
              }}
            >
              <Icons.EmailIcon style={{ position: 'absolute', left: '12px' }} />
              <>{t('Continue with Email')}</>
            </SocialLoginButton>
            {socialError ? (
              <SocialLoginError role="alert" aria-live="polite">
                {t('Something went wrong. Please try again.')}
              </SocialLoginError>
            ) : null}
          </>
        ) : (
          <SignInWithEmailScreen
            onBack={resetAuthScreens}
            onSuccess={handleLoginSuccess}
            onSignInWithPassword={openPasswordSignInScreen}
          />
        )}
        <h4
          style={{
            marginBottom: 0,
            marginTop: (showEmailScreen || showPasswordScreen) ? 'auto' : '16px',
            ...lightTheme.typography.Small12medium,
            lineHeight: '22px',
            color: 'rgba(153, 153, 153, 1)',
            fontWeight: 400,
          }}
        >
          {t('By signing up, you agree to our')}{' '}
          <span
            style={{ fontWeight: 500, cursor: 'pointer' }}
            onClick={() => {
              window.open('https://www.bazaart.com/terms/#page', '_blank')
            }}
          >
            {t('Terms')}
          </span>{' '}
          {t('and')}{' '}
          <span
            style={{ fontWeight: 500, cursor: 'pointer' }}
            onClick={() => {
              window.open('https://www.bazaart.com/terms/#privacy', '_blank')
            }}
          >
            {t('Privacy policy')}
          </span>
        </h4>
      </SignInForm>
      <SignInVisual $hasBackgroundMedia={Boolean(backgroundMedia)}>
        {backgroundMedia ? (
          <video
            width="385"
            height="385"
            style={{ objectFit: 'cover' }}
            loop
            autoPlay
            muted
            playsInline
            src={backgroundMedia.video}
            poster={backgroundMedia.poster}
          />
        ) : (
          <img src={BackgroundImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </SignInVisual>
    </SignInRoot>
  </>)
}

export default SignIn